// Event payload formatting — shared between EventLog (bottom-right feed)
// and OverviewTab (right-rail summary). Pure functions; no React. Strings
// come from the i18n dictionaries, so callers pass their `useT()` translator.

import { championEventDetail, championEventLabelKey } from '../../champion/championEventText.js';
import type { MessageKey, Translate } from '../../i18n/index.js';
import { houseName } from '../../sea/houseNames.js';
import { milestoneDetail, milestoneLabelKey } from './milestoneEventText.js';

export interface FormattableEvent {
  readonly id: number;
  readonly type: string;
  readonly payload?: Record<string, unknown>;
}

export const EVENT_LABEL_KEYS: Readonly<Record<string, MessageKey>> = {
  'doctrine:invested': 'event.doctrineInvested',
  'unit:moved': 'event.unitMoved',
  'unit:combat': 'event.unitCombat',
  'unit:garrisoned': 'event.unitGarrisoned',
  'unit:deployed': 'event.unitDeployed',
  'attack:city': 'event.cityAttacked',
  'attack:city_destroyed': 'event.cityDestroyed',
  'attack:city_damaged': 'event.cityDamaged',
  'building:started': 'event.buildingStarted',
  'building:completed': 'event.buildingCompleted',
  'hero:ability_activated': 'event.heroAbilityActivated',
  'hero:ability_selected': 'event.heroAbilitySelected',
  'intent:trigger': 'event.trigger',
  'sea:merchant_docked': 'event.merchantDocked',
  'sea:merchant_route': 'event.merchantRoute',
  'sea:merchant_accepted': 'event.merchantAccepted',
  'sea:merchant_declined': 'event.merchantDeclined',
};

/** Display label for an event type; unknown types show their raw id. */
export function eventLabel(t: Translate, type: string, payload?: Record<string, unknown>): string {
  const key =
    EVENT_LABEL_KEYS[type] ??
    championEventLabelKey(type, payload) ??
    (type === 'milestone:reached' ? milestoneLabelKey(payload) : null);
  return key ? t(key) : type;
}

const EVENT_ARCHETYPES = new Set(['industry', 'science', 'faith', 'arcane', 'nature', 'chaos']);

function coord(value: unknown): string {
  if (!value) return '?';
  const c = value as Record<string, number>;
  return `${c.q},${c.r}`;
}

export function formatPayload(
  t: Translate,
  type: string,
  payload?: Record<string, unknown>,
): string {
  if (!payload) return '';
  const champion = championEventDetail(t, type, payload);
  if (champion !== null) return champion;
  switch (type) {
    case 'milestone:reached':
      return milestoneDetail(t, payload);
    case 'doctrine:invested': {
      const archetype = String(payload.archetype);
      const archetypeKey = `archetype.${archetype}` as MessageKey;
      return t('event.detail.doctrineInvested', {
        player: (payload.playerIndex as number) + 1,
        archetype: EVENT_ARCHETYPES.has(archetype) ? t(archetypeKey) : archetype,
      });
    }
    case 'unit:moved':
      return t('event.detail.unitMoved', { from: coord(payload.from), to: coord(payload.to) });
    case 'unit:combat':
      return t('event.detail.unitCombat', { to: coord(payload.to) });
    case 'unit:garrisoned':
      return t('event.detail.unitGarrisoned', { coord: coord(payload.cityCoord) });
    case 'unit:deployed':
      return t('event.detail.unitDeployed', { coord: coord(payload.cityCoord) });
    case 'attack:city':
    case 'attack:city_damaged':
      return t('event.detail.damage', { damage: String(payload.damage ?? '?') });
    case 'attack:city_destroyed':
      return t('event.detail.cityRazed');
    case 'hero:ability_activated':
      return t('event.detail.abilityActivated', {
        slot: String((payload.slotIndex as number) ?? '?'),
        ability: String(payload.abilityName ?? payload.abilityId),
      });
    case 'hero:ability_selected':
      return t('event.detail.abilitySelected', {
        ability: String(payload.abilityName ?? payload.abilityId),
      });
    case 'sea:merchant_docked':
      return t('event.detail.merchantDocked', {
        house: t(houseName(Number(payload.house ?? 0))),
        gold: String(payload.goldPerTick ?? 0),
        grain: String(payload.grainPerTick ?? 0),
        gift: String(payload.gift ?? 0),
      });
    case 'sea:merchant_route':
      return t('event.detail.merchantRoute', {
        house: t(houseName(Number(payload.house ?? 0))),
        gold: String(payload.goldPerTick ?? 0),
        grain: String(payload.grainPerTick ?? 0),
      });
    case 'sea:merchant_accepted':
      return t('event.detail.merchantAccepted', {
        house: t(houseName(Number(payload.house ?? 0))),
      });
    default:
      return '';
  }
}
